import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../../api'
import ImpactModal from './ImpactModal'
import VisuelProduit from './VisuelProduit'
import { ORANGE, TEAL, VERT, eur, fmtDate } from './paniersProd'

/**
 * Saisie des dispos (proto v7.3, palier 2) : steppers − / + par produit,
 * ✕ retirer (sans reservation), feuille « + Proposer un produit » depuis mes
 * offres Paniers (unitaire + Tout ajouter). Le PLAFOND du stock partage est
 * verifie au backend (regle B) — l'UI le reflete (toast a la limite).
 * Reduction sous le reserve / retrait avec reservations : bloques (palier 3).
 */

// Pill translucide et discrete (proto v7.3) : le retrait ne doit pas crier —
// c'est une action ordinaire, pas un danger (retour Enzo 23 juil).
const Pill = ({ onClick, disabled, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="rounded-full text-[11px] font-extrabold disabled:opacity-40"
    style={{
      padding: '7px 13px',
      border: '1px solid rgba(7,62,61,0.18)',
      background: 'rgba(255,255,255,0.4)',
      color: 'rgba(7,62,61,0.65)',
    }}
  >
    {children}
  </button>
)

/** Stepper du proto v7.3 : carres 34x34, le + est ORANGE (gris au plafond). */
function Stepper({ valeur, min, max, unite, onChange }) {
  const auPlafond = max != null && valeur >= max
  const btn = (dir, disabled) => (
    <button
      onClick={() => onChange(valeur + dir)}
      disabled={disabled}
      className="font-extrabold flex items-center justify-center disabled:opacity-35"
      style={{
        width: 34,
        height: 34,
        borderRadius: 12,
        border: 'none',
        fontSize: 17,
        color: VERT,
        background: dir > 0 ? (auPlafond ? 'rgba(7,62,61,0.12)' : ORANGE) : 'rgba(7,62,61,0.08)',
      }}
    >
      {dir > 0 ? '+' : '−'}
    </button>
  )
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      {btn(-1, valeur <= min)}
      <span
        className="text-[15px] font-extrabold text-center"
        style={{ color: valeur > 0 ? VERT : 'rgba(7,62,61,0.35)', minWidth: 44 }}
      >
        {valeur} {unite}
      </span>
      {btn(+1, auPlafond)}
    </div>
  )
}

/** Feuille de selection depuis mes offres Paniers (glass-dark, createPortal). */
function FeuilleSelection({ restantes, onAdd, onAddAll, onClose, busy }) {
  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ background: 'rgba(4,25,24,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-dark w-full overflow-y-auto overscroll-contain"
        style={{ maxWidth: 448, maxHeight: '78dvh', borderRadius: '26px 26px 0 0', padding: '20px 18px 26px', color: 'rgb(var(--c-creme))' }}
      >
        <p className="font-serif text-[21px]">Proposer un produit</p>
        <p className="text-[12px] mt-0.5" style={{ color: 'rgba(247,240,234,0.7)' }}>
          Vos offres Paniers — un seul stock, partagé entre canaux et tournées.
        </p>
        {restantes.length === 0 ? (
          <p className="text-[13px] mt-4" style={{ color: 'rgba(247,240,234,0.75)' }}>
            Toutes vos offres Paniers sont déjà sur la tournée. Gérez vos offres depuis l'onglet 📦.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2 mt-3.5">
              {restantes.map((o) => {
                const epuise = o.plafond != null && o.plafond <= 0
                return (
                  <div key={o.productOfferId} className="rounded-[16px] flex items-center gap-3" style={{ padding: '11px 13px', background: 'rgba(255,255,255,0.07)' }}>
                    <VisuelProduit photo={o.photo} emoji={o.emoji} taille={40} sombre />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-bold truncate">{o.produit}</p>
                      <p className="text-[10.5px]" style={{ color: 'rgba(247,240,234,0.6)' }}>
                        {o.prix != null ? `Net ${eur(o.prix)} / ${o.unite}` : "Prix Paniers à saisir (✏️ dans Offres)"}
                        {o.fluxTendu ? ' · flux tendu' : o.plafond != null ? ` · ${o.plafond} dispo` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => onAdd(o)}
                      disabled={busy || epuise}
                      className="rounded-full text-[11.5px] font-extrabold flex-shrink-0 disabled:opacity-40"
                      style={{ padding: '7px 13px', border: 'none', background: TEAL, color: '#04302f' }}
                    >
                      {epuise ? 'Épuisé' : '+ Ajouter'}
                    </button>
                  </div>
                )
              })}
            </div>
            <button
              onClick={onAddAll}
              disabled={busy || restantes.every((o) => o.plafond != null && o.plafond <= 0)}
              className="w-full mt-3 rounded-2xl text-[13px] font-extrabold disabled:opacity-40"
              style={{ padding: 13, border: `1.5px solid ${TEAL}`, background: 'transparent', color: '#7ee8d8' }}
            >
              Tout ajouter
            </button>
          </>
        )}
        <button
          onClick={onClose}
          className="w-full mt-2 rounded-2xl text-[13px] font-bold"
          style={{ padding: 12, border: 'none', background: 'rgba(255,255,255,0.1)', color: 'rgb(var(--c-creme))' }}
        >
          Fermer
        </button>
      </div>
    </div>,
    document.body,
  )
}

function DisposEditor({ tourId, onToast, onChanged }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [sheet, setSheet] = useState(false)
  // Modale d'impact (P3) : { ligne, quantiteVoulue } quand on passe sous le reserve
  const [impact, setImpact] = useState(null)
  // Brouillon local par ligne (retour Enzo 23 juil) : on ajuste librement avec
  // les steppers, PUIS on valide. Rien ne part au serveur avant « Valider », et
  // la modale d'impact n'apparait qu'a ce moment-la.
  const [brouillon, setBrouillon] = useState({})

  useEffect(() => {
    if (!tourId) return
    setLoading(true)
    api.producer.getTourDispos(tourId)
      .then((d) => { setData(d); setBrouillon({}) })
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [tourId])

  const maj = (promesse) => {
    setBusy(true)
    return promesse
      .then((d) => { setData(d); setBrouillon({}); onChanged?.() })
      .catch((e) => onToast?.(e.message || 'La modification a échoué.'))
      .finally(() => setBusy(false))
  }

  if (loading) return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />
  if (!data) return (
    <div className="glass-surface rounded-3xl p-6 text-center">
      <span className="text-4xl block mb-2">📋</span>
      <p className="text-[13px] text-ink/65">Saisie indisponible pour le moment.</p>
    </div>
  )

  const { lignes, restantes, tour } = data
  const commandesOuvertes = tour.statut === 'COMMANDES'

  return (
    <>
      <p className="text-[11px] font-bold uppercase mb-2 mx-0.5" style={{ letterSpacing: 2, color: TEAL }}>
        Mes disponibilités ({lignes.length})
      </p>
      {lignes.length === 0 && (
        <div className="glass-surface rounded-3xl p-6 text-center">
          <span className="text-4xl block mb-2">📋</span>
          <p className="text-[13px] text-ink/65">
            La tournée démarre vide : choisissez vos produits parmi vos offres Paniers.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {lignes.map((l) => {
          const valeur = brouillon[l.tourOfferId] ?? l.dispo
          const modifie = valeur !== l.dispo
          const sousLeReserve = valeur < l.vendu
          const auPlafond = l.plafond != null && valeur >= l.plafond
          return (
            <div key={l.tourOfferId} className="glass-surface rounded-[18px]" style={{ padding: '13px 15px' }}>
              <div className="flex items-center gap-3">
                <VisuelProduit photo={l.photo} emoji={l.emoji} taille={44} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: VERT }}>{l.produit}</p>
                  <p className="text-[11px] text-ink/50">
                    Net : {eur(l.prix)} / {l.unite}
                    {l.vendu > 0 ? ` · réservé : ${l.vendu} ${l.unite}` : ''}
                  </p>
                  {l.plafond != null && (
                    <p className="text-[10.5px] mt-0.5" style={{ color: auPlafond ? ORANGE : 'rgba(7,62,61,0.45)' }}>
                      Stock partagé : {l.plafond} {l.unite}{auPlafond ? ' — limite atteinte' : ''}
                    </p>
                  )}
                </div>
                <Stepper
                  valeur={valeur}
                  min={0}
                  max={l.plafond}
                  unite={l.unite}
                  onChange={(q) => {
                    if (busy) return
                    if (l.plafond != null && q > l.plafond) {
                      onToast?.(`Stock partagé atteint : ${l.plafond} ${l.unite} max pour cette tournée.`)
                      return
                    }
                    setBrouillon((b) => ({ ...b, [l.tourOfferId]: q }))
                  }}
                />
              </div>
              {l.vendu > 0 && (
                <p className="text-[10.5px] mt-2" style={{ color: sousLeReserve ? ORANGE : '#0a8a5f' }}>
                  {sousLeReserve
                    ? `⚠️ Sous les ${l.vendu} ${l.unite} réservés — Manrina devra valider.`
                    : `${l.vendu} ${l.unite} déjà réservés.`}
                </p>
              )}

              {/* Valider n'apparait que si la ligne a bouge (retour Enzo 23 juil) :
                  on ajuste tranquillement, puis on decide. */}
              <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                {modifie && (
                  <>
                    <button
                      disabled={busy}
                      onClick={() => {
                        if (sousLeReserve) {
                          setImpact({ ligne: l, quantiteVoulue: valeur })
                          return
                        }
                        maj(api.producer.setTourDispo(tourId, l.tourOfferId, valeur))
                      }}
                      className="rounded-full text-[11.5px] font-extrabold disabled:opacity-40"
                      style={{
                        padding: '7px 14px',
                        border: 'none',
                        background: sousLeReserve ? ORANGE : TEAL,
                        color: '#04302f',
                      }}
                    >
                      {sousLeReserve ? `Demander l'ajustement` : `Valider ${valeur} ${l.unite}`}
                    </button>
                    <Pill
                      disabled={busy}
                      onClick={() => setBrouillon((b) => {
                        const n = { ...b }
                        delete n[l.tourOfferId]
                        return n
                      })}
                    >
                      Annuler
                    </Pill>
                  </>
                )}
                {!modifie && (
                  <Pill
                    disabled={busy}
                    onClick={() =>
                      l.vendu > 0
                        ? setImpact({ ligne: l, quantiteVoulue: 0 })
                        : maj(api.producer.removeTourDispo(tourId, l.tourOfferId))
                    }
                  >
                    ✕ Retirer de la tournée
                  </Pill>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <button
        onClick={() => setSheet(true)}
        className="w-full mt-3 rounded-2xl text-[13.5px] font-extrabold text-white"
        style={{ padding: 14, border: 'none', background: `linear-gradient(135deg, ${ORANGE}, #e8703a)`, boxShadow: '0 8px 24px rgba(244,137,83,0.35)' }}
      >
        + Proposer un produit
      </button>

      <p className="text-[10.5px] leading-relaxed mt-2.5 mx-0.5 text-ink/45">
        {commandesOuvertes
          ? `🦊 Commandes ouvertes : vous pouvez encore ajouter ou augmenter. Réduire sous le réservé passera par une demande d'ajustement Manrina.`
          : `🦊 Ajustable librement jusqu'à l'ouverture des commandes${tour.dateClotureCommandes ? ` — clôture ${fmtDate(tour.dateClotureCommandes)}` : ''}. Un seul stock, partagé entre vos canaux.`}
      </p>

      {impact && (
        <ImpactModal
          tourId={tourId}
          ligne={impact.ligne}
          quantiteVoulue={impact.quantiteVoulue}
          onClose={() => setImpact(null)}
          onToast={onToast}
          onEnvoye={() => {
            onToast?.('Demande envoyée à Manrina — vous serez prévenu de la décision.')
            maj(api.producer.getTourDispos(tourId))
          }}
        />
      )}

      {sheet && (
        <FeuilleSelection
          restantes={restantes}
          busy={busy}
          onClose={() => setSheet(false)}
          onAdd={(o) => maj(api.producer.addTourDispo(tourId, o.productOfferId, o.plafond != null ? Math.min(1, o.plafond) : 1))}
          onAddAll={async () => {
            setBusy(true)
            try {
              let d = data
              for (const o of restantes) {
                if (o.plafond != null && o.plafond <= 0) continue
                d = await api.producer.addTourDispo(tourId, o.productOfferId, o.plafond != null ? Math.min(1, o.plafond) : 1)
              }
              setData(d)
              onChanged?.()
            } catch (e) {
              onToast?.(e.message || `L'ajout a échoué.`)
            } finally {
              setBusy(false)
            }
          }}
        />
      )}
    </>
  )
}

export default DisposEditor
